function getCartDrawerSectionsToRender() {
  const sections = [
    {
      section: 'CartDrawer',
      id: 'cart-drawer',
      selector: '[data-cart-drawer-dynamic]',
    },
    {
      section: 'CartDrawer',
      id: 'cart-drawer',
      selector: '[data-cart-drawer-free-shipping]',
    },
    {
      section: 'cart-icon-bubble',
      id: 'cart-icon-bubble',
      selector: '.shopify-section',
      targetId: 'cart-icon-bubble',
    },
  ];

  return sections;
}

function getCartDrawerSectionTarget(section, root) {
  if (section.targetId) {
    const externalTarget = document.getElementById(section.targetId);
    if (!externalTarget) return null;
    return externalTarget.querySelector(section.selector) || externalTarget;
  }

  return root?.querySelector(section.selector) ?? null;
}

function isCartDrawerEmptyState(parsedState) {
  if (typeof parsedState?.item_count === 'number') {
    return parsedState.item_count === 0;
  }
  if (Array.isArray(parsedState?.items)) {
    return parsedState.items.length === 0;
  }
  // /cart/add.js returns the added line item (has quantity) without item_count.
  if (parsedState?.quantity != null || parsedState?.id != null) {
    return false;
  }
  return false;
}

function renderCartDrawerSections(parsedState, root = document.getElementById('CartDrawer')) {
  if (!parsedState?.sections) return;

  getCartDrawerSectionsToRender().forEach((section) => {
    const sectionHtml = parsedState.sections[section.id];
    if (!sectionHtml) return;

    const target = getCartDrawerSectionTarget(section, root);
    if (!target) return;

    const innerHtml = getCartDrawerSectionInnerHTML(sectionHtml, section.selector);
    if (innerHtml === null) return;

    target.innerHTML = innerHtml;
  });

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    cartDrawer.classList.toggle('is-empty', isCartDrawerEmptyState(parsedState));
    cartDrawer.updateProductRecommendation?.();
  }

  document.dispatchEvent(
    new CustomEvent('afterCartChanged', {
      detail: { source: 'cart-drawer', cartData: parsedState },
    })
  );
}

function getCartDrawerSectionInnerHTML(html, selector) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const element =
    parsed.querySelector(selector) ||
    parsed.querySelector('[id^="shopify-section-"]') ||
    parsed.body.firstElementChild;
  return element ? element.innerHTML : null;
}

class CartDrawer extends DrawerFixed {
  constructor() {
    super();

    this.onOverlayClick = this.close.bind(this);
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.onOverlayClick);
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }
    this.updateProductRecommendation();
    super.open(triggeredBy);
  }

  close(evt) {
    const currentFooterAction = this.querySelector('cart-drawer-footer-action.open');
    if (currentFooterAction) {
      currentFooterAction.closeActionContent();
    }
    if (!this.classList.contains('busy') || !evt?.target?.closest('cart-drawer-footer-action')) {
      super.close(evt);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.toggle('is-empty', isCartDrawerEmptyState(parsedState));
    this.productId = parsedState.id;
    renderCartDrawerSections(parsedState, document.getElementById('CartDrawer'));

    setTimeout(() => {
      this.open();
    });
  }

  updateProductRecommendation() {
    this.productRecommendation = this.querySelector('cart-product-recommendations');
    if (!this.productRecommendation) return;

    const cartItemsWrapper = this.querySelector('.cart-drawer__items');
    if (cartItemsWrapper) {
      cartItemsWrapper.classList.toggle(
        'cart-drawer__items--large',
        cartItemsWrapper.querySelectorAll('.cart-item').length > 1
      );
    }
    this.productRecommendation.update();
  }

  getSectionsToRender() {
    // product-form only needs unique section ids; rendering uses getCartDrawerSectionsToRender().
    const seen = new Set();
    return getCartDrawerSectionsToRender()
      .filter((section) => {
        if (seen.has(section.id)) return false;
        seen.add(section.id);
        return true;
      })
      .map((section) => ({
        id: section.id,
        selector: section.selector,
      }));
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return getCartDrawerSectionsToRender();
  }

  getSectionIdsToRequest() {
    return [...new Set(this.getSectionsToRender().map((section) => section.id))];
  }

  renderContents(parsedState, lineItem, name, line) {
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartPage = document.getElementById('main-cart');

    if (cartPage) {
      cartPage.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartPageBox = cartPage.getBoundingClientRect();
      if (!(cartPageBox.top < window.innerHeight && cartPageBox.bottom >= 0)) {
        cartPage.scrollIntoView({ behavior: 'smooth' });
      }
    }

    renderCartDrawerSections(parsedState, document.getElementById('CartDrawer'));

    const lineIndex = line || lineItem?.querySelector?.('[data-index]')?.dataset?.index;
    const freshLineItem = lineIndex
      ? document.getElementById(`CartDrawer-Item-${lineIndex}`)
      : null;
    const shouldFocusQuantityControls = name === 'plus' || name === 'minus';
    const focusTarget = shouldFocusQuantityControls
      ? freshLineItem?.querySelector(`[name="${name}"]`)
      : null;

    if (focusTarget && cartDrawerWrapper) {
      trapFocus(cartDrawerWrapper, focusTarget);
    } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
      trapFocus(
        cartDrawerWrapper.querySelector('.drawer__inner-empty'),
        cartDrawerWrapper.querySelector('a')
      );
    } else if (cartDrawerWrapper && !shouldFocusQuantityControls) {
      const fallback =
        cartDrawerWrapper.querySelector('.cart-item-secondary__title') ||
        cartDrawerWrapper.querySelector('.cart-item__name');
      if (fallback) trapFocus(cartDrawerWrapper, fallback);
    }
  }

  updateProductRecommendation() {
    document.querySelector('cart-drawer')?.updateProductRecommendation();
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerFooterAction extends HTMLElement {
  constructor() {
    super();
    this.openButton = this.querySelector('.cart-drawer__footer__action-label');
    this.content = this.querySelector('.cart-drawer__footer__action-content');
    this.closeButton = this.content.querySelector('.cart-drawer__footer__action-close');

    this.openButton.addEventListener('click', this.onClickFooterAction.bind(this));
    this.closeButton.addEventListener('click', this.closeActionContent.bind(this));

    this.cartDrawer = this.closest('.drawer__inner');
  }

  onClickFooterAction() {
    const isOpen = this.classList.contains('open');
    isOpen ? this.closeActionContent() : this.openActionContent();
  }

  openActionContent() {
    this.style.setProperty('--drawer-bottom-position', `${Math.floor(this.cartDrawer.scrollTop)}px`);
    this.classList.add('open');
    this.content.classList.add('appear');
    setTimeout(() => {
      this.content.classList.add('animate');
    }, 1);
    setTimeout(() => {
      this.content.querySelector('textarea,select')?.focus();
    }, 300);
    this.closest('cart-drawer')?.classList.add('busy');
  }

  closeActionContent() {
    this.classList.remove('open');
    this.content.classList.remove('animate');
    setTimeout(() => {
      this.content.classList.remove('appear');
      const cartDrawer = this.closest('cart-drawer');
      cartDrawer?.classList.remove('busy');
      if (cartDrawer && this.openButton) {
        trapFocus(cartDrawer, this.openButton);
      }
    }, 300);
  }
}

customElements.define('cart-drawer-footer-action', CartDrawerFooterAction);

class CartDrawerNote extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('textarea');
    this.button = this.querySelector('.button');

    this.button.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        const body = JSON.stringify({ note: this.input.value });
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        this.closest('cart-drawer-footer-action')?.closeActionContent();
      },
      false
    );
  }
}

customElements.define('cart-drawer-note', CartDrawerNote);
